'use client';

import { useEffect, useRef } from 'react';

/** What fades up on V1–V3: headings, body copy, step rows, plan cards, FAQ rows, callback. */
// CSS-module class names carry a hash (`FestiveStepRows-module__<hash>__row`), so match
// the module and the local name separately.
const SELECTOR = [
  'h2',
  'h3',
  '[class*="FestiveStepRows-module"][class*="__introBody"]',
  'li[class*="FestiveStepRows-module"]',
  '[class*="FestiveStepRows-module"][class*="__callback"]',
  '[class*="FestivePlanPanelsV2-module"][class*="__body"]',
  'article[data-plan-id]',
  '[data-festive-callback] p',
  '[data-festive-callback] button',
  '.faq-element',
].join(',');

/**
 * Subtle scroll reveal for the festive V1–V3 pages: each target starts transparent, 28px
 * low and slightly blurred (`data-fr="pending"`) and settles once it scrolls in
 * (`data-fr="in"`). Siblings stagger by 70ms. The hero (own entrance), the sticky bar,
 * and text inside plan cards (the card reveals as a whole) are left alone. CSS lives
 * with the page (.geistPage in festiveVersions.module.css). Reduced motion: no-op.
 */
export const useFestiveReveal = <T extends HTMLElement>(enabled: boolean) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const root = ref.current;
    if (
      !enabled ||
      !root ||
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }

    const targets = Array.from(
      root.querySelectorAll<HTMLElement>(SELECTOR),
    ).filter(
      (el) =>
        !el.closest('[data-pl-hero]') &&
        !el.closest('[data-festive-sticky]') &&
        !(el.closest('article[data-plan-id]') && el.tagName !== 'ARTICLE'),
    );

    const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
    const order = new Map<Element, number>();
    targets.forEach((el) => {
      const parent = el.parentElement;
      const index = parent ? order.get(parent) ?? 0 : 0;
      if (parent) order.set(parent, index + 1);
      el.style.setProperty('--fr-delay', `${Math.min(index, 5) * 70}ms`);
      const rect = el.getBoundingClientRect();
      if (rect.top < vh + 120 && rect.bottom > -120) {
        el.dataset.fr = 'in';
      } else {
        el.dataset.fr = 'pending';
      }
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          (entry.target as HTMLElement).dataset.fr = 'in';
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '120px 0px 120px 0px', threshold: 0 },
    );
    targets.forEach((el) => {
      if (el.dataset.fr === 'pending') {
        observer.observe(el);
      }
    });

    const fallbackTimer = setTimeout(() => {
      targets.forEach((el) => {
        el.dataset.fr = 'in';
      });
    }, 1500);

    return () => {
      clearTimeout(fallbackTimer);
      observer.disconnect();
      targets.forEach((el) => {
        delete el.dataset.fr;
        el.style.removeProperty('--fr-delay');
      });
    };
  }, [enabled]);

  return ref;
};
